import React, { useEffect } from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  keyframes,
  useMediaQuery,
  useTheme
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { openLoginPopup, closeLoginPopup } from "../../redux/slices/authSlice";
import assistantPic from "../../assets/images/assistant_pic1.jpg";

const sparkle = keyframes`
  0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.8; }
  50% { transform: scale(1.2) rotate(15deg); opacity: 1; }
`;

const fadeInUp = keyframes`
  0% { opacity: 0; transform: translateY(10px) scale(0.95); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const LoginPopup = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, token, isLoginPopupOpen } = useSelector((state) => state.auth);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  // Routes where we absolutely do NOT want to show the popup
  const hiddenRoutes = [
    "/login",
    "/signup",
    "/forgot-password",
    "/admin-login",
    "/verify-email",
    "/profile",
    "/dashboard",
    "/brand-login",
    "/dashboard-brands"
  ];

  useEffect(() => {
    const storedToken = localStorage.getItem("userToken");
    if (isAuthenticated || token || storedToken) {
      return;
    }

    if (hiddenRoutes.some(route => location.pathname.startsWith(route))) {
      return;
    }

    const hasSeenPopup = sessionStorage.getItem("hasSeenLoginPopup");
    if (hasSeenPopup) {
      return;
    }

    const timer = setTimeout(() => {
      dispatch(openLoginPopup());
      sessionStorage.setItem("hasSeenLoginPopup", "true");
    }, 2500);

    return () => clearTimeout(timer);
  }, [isAuthenticated, token, location.pathname, dispatch]);

  useEffect(() => {
    if (isLoginPopupOpen) {
      sessionStorage.setItem("hasSeenLoginPopup", "true");
    }
  }, [isLoginPopupOpen]);

  useEffect(() => {
    if (isAuthenticated && isLoginPopupOpen) {
      dispatch(closeLoginPopup());
    }
  }, [isAuthenticated, isLoginPopupOpen, dispatch]);

  useEffect(() => {
    if (hiddenRoutes.some(route => location.pathname.startsWith(route)) && isLoginPopupOpen) {
      dispatch(closeLoginPopup());
    }
  }, [location.pathname, isLoginPopupOpen, dispatch]);

  const handleClose = () => {
    dispatch(closeLoginPopup());
  };

  if (hiddenRoutes.some(route => location.pathname.startsWith(route))) {
    return null;
  }

  return (
    <Dialog
      open={Boolean(isLoginPopupOpen)}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: "24px",
          padding: 0,
          background: "transparent",
          boxShadow: "none",
          overflow: "visible",
          maxWidth: "450px",
          width: "90%",
        }
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
          }
        }
      }}
    >
      <Box
        sx={{
          position: "relative",
          background: "#FFFFFF",
          borderRadius: "24px",
          overflow: "hidden",
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
          border: "1px solid rgba(255, 140, 56, 0.2)",
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 10,
            color: "#666",
            background: "rgba(255,255,255,0.8)",
            "&:hover": {
              background: "#f0f0f0",
              color: "#000",
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* Top Section: Product Image and Animated Chat Bubbles */}
        <Box sx={{
          display: 'flex',
          gap: { xs: 1.5, sm: 2 },
          p: { xs: 2, sm: 3 },
          pt: { xs: 3, sm: 5 },
          background: "linear-gradient(180deg, #FAFAFA 0%, #FFFFFF 100%)",
          alignItems: 'center',
          borderBottom: "1px solid rgba(0,0,0,0.04)"
        }}>
          {/* Left: Product Image */}
          <Box
            sx={{
              position: "relative",
              flexShrink: 0
            }}
          >
            <Box
              component="img"
              src={assistantPic}
              alt="Product"
              sx={{
                width: { xs: 80, sm: 95 },
                height: "auto",
                objectFit: "contain",
                borderRadius: "12px",
                boxShadow: "0 8px 16px rgba(0,0,0,0.12)",
                border: "1px solid rgba(0,0,0,0.05)"
              }}
            />
            {/* Sparkle Icon overlapping the product image */}
            <Box
              sx={{
                position: "absolute",
                top: -12,
                left: -12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "#FFFFFF",
                boxShadow: "0 4px 8px rgba(255, 140, 56, 0.3)",
              }}
            >
              <AutoAwesomeIcon
                sx={{
                  color: "#FF8C38",
                  fontSize: 18,
                  animation: `${sparkle} 2s ease-in-out infinite`
                }}
              />
            </Box>
          </Box>

          {/* Right: Animated Chat Bubbles */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{
              px: { xs: 1.25, sm: 1.75 }, py: { xs: 0.75, sm: 1.25 },
              borderRadius: "16px 16px 4px 16px",
              background: "linear-gradient(135deg, #FF8C38 0%, #FF9B80 100%)",
              color: "#FFF",
              alignSelf: "flex-start",
              fontSize: { xs: "10px", sm: "12px" },
              fontFamily: "Poppins-Medium",
              animation: `${fadeInUp} 0.5s ease forwards`,
              opacity: 0,
              animationDelay: '0.2s',
              boxShadow: "0 4px 12px rgba(255, 140, 56, 0.2)"
            }}>
              Can you find similar options in red?
            </Box>
            <Box sx={{
              px: { xs: 1.25, sm: 1.75 }, py: { xs: 0.75, sm: 1.25 },
              borderRadius: "16px 16px 4px 16px",
              background: "linear-gradient(135deg, #FF8C38 0%, #FF9B80 100%)",
              color: "#FFF",
              alignSelf: "flex-end",
              fontSize: { xs: "10px", sm: "12px" },
              fontFamily: "Poppins-Medium",
              animation: `${fadeInUp} 0.5s ease forwards`,
              opacity: 0,
              animationDelay: '1.2s',
              boxShadow: "0 4px 12px rgba(255, 140, 56, 0.2)"
            }}>
              Show cheaper options
            </Box>
            <Box sx={{
              px: { xs: 1.25, sm: 1.75 }, py: { xs: 0.75, sm: 1.25 },
              borderRadius: "16px 16px 4px 16px",
              background: "linear-gradient(135deg, #FF8C38 0%, #FF9B80 100%)",
              color: "#FFF",
              alignSelf: "flex-start",
              fontSize: { xs: "10px", sm: "12px" },
              fontFamily: "Poppins-Medium",
              animation: `${fadeInUp} 0.5s ease forwards`,
              opacity: 0,
              animationDelay: '2.2s',
              boxShadow: "0 4px 12px rgba(255, 140, 56, 0.2)"
            }}>
              Find me trousers to pair with this
            </Box>
          </Box>
        </Box>

        {/* Text Section */}
        <Box sx={{ p: { xs: 1, sm: 1 }, pb: { xs: 1, sm: 2 }, textAlign: "center", background: "#FFFFFF" }}>
          <Typography
            variant="h5"
            sx={{
              color: "#1A1A1A",
              fontWeight: 700,
              fontFamily: "Poppins-SemiBold",
              fontSize: { xs: "18px", sm: "24px" },
              mb: 1,
              lineHeight: 1.2
            }}
          >
            Access Exclusive Features
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#666",
              fontFamily: "Poppins-Regular",
              fontSize: { xs: "11px", sm: "13px" },
              maxWidth: "90%",
              margin: "0 auto",
            }}
          >
            Chat with the Handpik Assistant, ask about specific products, and get personalized styling recommendations.
          </Typography>
        </Box>

        {/* Action Section */}
        <Box sx={{ p: { xs: 3, sm: 4 }, pt: { xs: 1, sm: 2 }, textAlign: "center", background: "#FFFFFF" }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              handleClose();
              navigate("/login");
            }}
            sx={{
              py: 1.5,
              borderRadius: "12px",
              background: "#1A1A1A",
              color: "#FFFFFF",
              fontWeight: 600,
              fontFamily: "Poppins-SemiBold",
              textTransform: "none",
              fontSize: "16px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              "&:hover": {
                background: "#000000",
                boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s ease"
            }}
          >
            Log In Now
          </Button>

          <Typography
            variant="body2"
            sx={{
              mt: 2.5,
              color: "#666",
              fontFamily: "Poppins-Regular"
            }}
          >
            New to Handpik?{" "}
            <Box
              component="span"
              onClick={() => {
                handleClose();
                navigate("/signup");
              }}
              sx={{
                color: "#FF8C38",
                fontWeight: 600,
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                }
              }}
            >
              Create an Account
            </Box>
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );
};

export default LoginPopup;
